// Lobby — list campaigns, create new, delete. Entry point of Hearth.
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { CreateGameResponse, DmStyle, GameBrief } from "../lib/types";
import { DM_STYLES } from "../lib/types";
import { DM_STYLE_BLURB } from "../lib/srd";
import { friendlyDate } from "../lib/format";
import { CenterLoad, Modal } from "./ui";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../hooks/useAuth";

// A pending destructive action awaiting styled confirmation.
type Pending = { kind: "delete" | "restart"; game: GameBrief };

export function Lobby({ onOpen, onSignIn }: { onOpen: (gameId: string) => void; onSignIn?: () => void }) {
  const toast = useToast();
  const { user, logout } = useAuth();
  const [games, setGames] = useState<GameBrief[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  async function load() {
    try {
      const r = await api.games();
      setGames(r.games);
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to load campaigns", "error");
      setGames([]);
    }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  async function doDelete(id: string) {
    try {
      await api.deleteGame(id);
      setGames((g) => (g ?? []).filter((x) => x.id !== id));
      toast.push("Campaign deleted", "info");
    } catch {
      toast.push("Failed to delete", "error");
    }
  }

  async function doRestart(id: string) {
    try {
      await api.restartGame(id);
      toast.push("Campaign restarted — fresh opening", "success");
      load();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "Failed to restart", "error");
    }
  }

  return (
    <div className="screen">
      <div className="container">
        <div className="lobby-userbar">
          {user ? (
            <>
              <span>Signed in as <span className="who">{user.display_name}</span></span>
              <button className="lobby-signin" onClick={() => logout()}>Sign out</button>
            </>
          ) : (
            <>
              <span>Browsing as a guest</span>
              {onSignIn && <button className="lobby-signin" onClick={onSignIn}>Sign in</button>}
            </>
          )}
        </div>

        <div className="lobby-hero">
          <h1>Gather at the <span className="accent">Hearth</span></h1>
          <p>An AI-run table for Dungeons &amp; Dragons. Build a party of heroes — living or imagined — and let the story unfold.</p>
        </div>

        {games === null ? (
          <CenterLoad label="Loading campaigns…" />
        ) : (
          <div className="lobby-grid">
            <div className="card game-card game-card__new" onClick={() => setCreating(true)}>
              <div className="plus">＋</div>
              <div>Begin a new campaign</div>
            </div>
            {games.map((g) => {
              const open = g.open_seats ?? 0;
              const canManage = g.is_owner !== false; // owner (or legacy ownerless) may manage
              return (
                <div key={g.id} className="card game-card" onClick={() => onOpen(g.id)}>
                  {canManage && (
                    <CardOverflow
                      onRestart={() => setPending({ kind: "restart", game: g })}
                      onDelete={() => setPending({ kind: "delete", game: g })}
                    />
                  )}
                  <h3>{g.name}</h3>
                  <div className="scene">{g.scene || "An untold story awaits…"}</div>
                  <div className="game-card__meta">
                    {g.joined && <span className="game-badge game-badge--joined">Joined</span>}
                    {g.is_owner && <span className="game-badge game-badge--own">Host</span>}
                    {open > 0
                      ? <span className="game-badge game-badge--open">{open} open {open === 1 ? "seat" : "seats"}</span>
                      : <span className="game-badge game-badge--full">Full</span>}
                    <span className="chip">{g.players} {g.players === 1 ? "seat" : "seats"} filled</span>
                  </div>
                  <div className="meta-row">
                    {g.owner_name && <span>Hosted by {g.owner_name}</span>}
                    {g.created && <span>{friendlyDate(g.created)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {creating && (
        <CreateGameModal
          onClose={() => setCreating(false)}
          onCreated={(r) => { setCreating(false); onOpen(r.id); }}
        />
      )}

      {pending?.kind === "restart" && (
        <ConfirmRestartModal
          game={pending.game}
          onClose={() => setPending(null)}
          onConfirm={async () => { const id = pending.game.id; setPending(null); await doRestart(id); }}
        />
      )}
      {pending?.kind === "delete" && (
        <ConfirmDeleteModal
          game={pending.game}
          onClose={() => setPending(null)}
          onConfirm={async () => { const id = pending.game.id; setPending(null); await doDelete(id); }}
        />
      )}
    </div>
  );
}

// Per-card overflow menu — tucks destructive actions off the card face behind a
// ⋯ button so a stray click can't nuke a campaign.
function CardOverflow({ onRestart, onDelete }: { onRestart: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div className="game-card__btns" ref={ref} style={{ position: "relative" }}
      onClick={(e) => e.stopPropagation()}>
      <button className="card-del" title="Manage campaign" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}>⋯</button>
      {open && (
        <div className="card game-card__menu" role="menu"
          style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 30,
            minWidth: 150, padding: 6, display: "flex", flexDirection: "column", gap: 2 }}>
          <button className="btn btn--ghost btn--sm" role="menuitem"
            style={{ justifyContent: "flex-start" }}
            onClick={() => { setOpen(false); onRestart(); }}>↻ Restart…</button>
          <button className="btn btn--ghost btn--sm btn--danger" role="menuitem"
            style={{ justifyContent: "flex-start" }}
            onClick={() => { setOpen(false); onDelete(); }}>✕ Delete…</button>
        </div>
      )}
    </div>
  );
}

function ConfirmRestartModal({ game, onClose, onConfirm }: {
  game: GameBrief; onClose: () => void; onConfirm: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal onClose={onClose}>
      <h2>Restart “{game.name}”?</h2>
      <div className="modal__sub">This starts the campaign over from a fresh opening.</div>
      <ul className="muted" style={{ fontSize: 13, lineHeight: 1.6, paddingLeft: 18, margin: "4px 0 16px" }}>
        <li>The story log and any active combat are <b>cleared</b>.</li>
        <li>Every character is <b>reset to full</b> health and resources.</li>
        <li>The party and their character sheets are <b>kept</b>.</li>
      </ul>
      <div className="modal__row">
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary" disabled={busy}
          onClick={async () => { setBusy(true); await onConfirm(); }}>
          {busy ? "Restarting…" : "Restart campaign"}
        </button>
      </div>
    </Modal>
  );
}

function ConfirmDeleteModal({ game, onClose, onConfirm }: {
  game: GameBrief; onClose: () => void; onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const match = typed.trim() === game.name.trim();
  return (
    <Modal onClose={onClose}>
      <h2>Delete “{game.name}”?</h2>
      <div className="modal__sub">This permanently removes the campaign, its party, and its story. This cannot be undone.</div>
      <div className="form-row">
        <label className="label">Type the campaign name to confirm</label>
        <input className="field" value={typed} autoFocus
          placeholder={game.name}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && match && !busy) { setBusy(true); onConfirm(); } }} />
      </div>
      <div className="modal__row">
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary btn--danger" disabled={!match || busy}
          title={match ? "Delete permanently" : "Type the exact campaign name to enable"}
          onClick={async () => { setBusy(true); await onConfirm(); }}>
          {busy ? "Deleting…" : "Delete forever"}
        </button>
      </div>
    </Modal>
  );
}

function CreateGameModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: CreateGameResponse) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [setting, setSetting] = useState("");
  const [scene, setScene] = useState("");
  const [style, setStyle] = useState<DmStyle>("balanced");
  const [tone, setTone] = useState("");
  const [seats, setSeats] = useState(4);
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const [busy, setBusy] = useState(false);

  async function create() {
    if (!name.trim()) { toast.push("Name your campaign", "error"); return; }
    setBusy(true);
    try {
      const r = await api.createGame({
        name: name.trim(), setting, scene, dm_style: style, tone_notes: tone, num_seats: seats, visibility,
      });
      toast.push("Campaign created", "success");
      onCreated(r);
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to create", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2>Begin a campaign</h2>
      <div className="modal__sub">Set the premise. The AI Dungeon Master will bring it to life.</div>

      <div className="form-row">
        <label className="label">Campaign name</label>
        <input className="field" value={name} autoFocus onChange={(e) => setName(e.target.value)}
          placeholder="The Sunless Road" />
      </div>
      <div className="form-row">
        <label className="label">Setting / premise</label>
        <textarea className="field" value={setting} onChange={(e) => setSetting(e.target.value)}
          placeholder="A muddy trade road through the Mirewood at dusk. Bandits prey on caravans; the party guards a wagon of grain…" />
      </div>
      <div className="form-row">
        <label className="label">Opening scene (optional)</label>
        <input className="field" value={scene} onChange={(e) => setScene(e.target.value)}
          placeholder="Dusk on the Sunless Road, fog curling between the trees." />
      </div>
      <div className="form-row">
        <label className="label">DM tone</label>
        <div className="style-pills">
          {DM_STYLES.map((s) => (
            <button key={s} className={`style-pill${style === s ? " style-pill--on" : ""}`}
              onClick={() => setStyle(s)} title={DM_STYLE_BLURB[s]}>{s}</button>
          ))}
        </div>
        <div className="hint">{DM_STYLE_BLURB[style]}</div>
      </div>
      <div className="form-row">
        <label className="label">Tone notes (optional)</label>
        <input className="field" value={tone} onChange={(e) => setTone(e.target.value)}
          placeholder="Cinematic and brisk, with moments of dry humor." />
      </div>
      <div className="form-row">
        <label className="label">Seats at the table — {seats}</label>
        <input type="range" min={1} max={6} value={seats} style={{ width: "100%" }}
          onChange={(e) => setSeats(Number(e.target.value))} />
      </div>
      <div className="form-row">
        <label className="label">Visibility</label>
        <div className="style-pills">
          <button className={`style-pill${visibility === "public" ? " style-pill--on" : ""}`}
            onClick={() => setVisibility("public")}>public</button>
          <button className={`style-pill${visibility === "private" ? " style-pill--on" : ""}`}
            onClick={() => setVisibility("private")}>private</button>
        </div>
        <div className="hint">{visibility === "public"
          ? "Listed in the open lobby — anyone can find it and claim a seat."
          : "Hidden from the lobby — only you and players you invite (by link) can join."}</div>
      </div>

      <div className="modal__row">
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary btn--lg" disabled={busy} onClick={create}>
          {busy ? "Creating…" : "Light the Hearth"}
        </button>
      </div>
    </Modal>
  );
}
